import React, { useEffect, useState } from "react";
import {
  Dialog,
  Box,
  Slide,
  IconButton,
  Button,
  useMediaQuery,
  useTheme,
  CircularProgress,
} from "@mui/material";
import { useSelector } from "react-redux";
import {
  DARK_THEME_BODY_COLOR,
  DARK_THEME_TEXT_COLOR,
  LIGHT_THEME_TEXT_COLOR,
  SUPPORTED_IMG_FILE_TYPES,
  SUPPORTED_DOC_FILE_TYPES,
} from "../../constants";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import ThemedTypography from "../ThemedTypography";
import ThemedDialogTitle from "./ThemedDialogTitle";
import ThemedDialogContent from "./ThemedDialogContent";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export default function MobileViewFileDialog({
  file,
  url,
  handleClose,
  files = [],
  currentFileIndex = 0,
  onNavigateToFile,
}) {
  const darkThemeEnabled = useSelector((state) => state.darkThemeEnabled);
  const theme = useTheme();
  const [loading, setLoading] = useState(true);

  // Detect if we're on a mobile device
  const isMobile =
    useMediaQuery(theme.breakpoints.only("xs")) ||
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent,
    );

  const handleOpenInNewTab = () => {
    window.open(url, "_blank");
  };

  const handleNavigateToPrevious = () => {
    if (onNavigateToFile && files.length > 1) {
      const newIndex =
        currentFileIndex > 0 ? currentFileIndex - 1 : files.length - 1;
      onNavigateToFile(newIndex);
    }
  };

  const handleNavigateToNext = () => {
    if (onNavigateToFile && files.length > 1) {
      const newIndex =
        currentFileIndex < files.length - 1 ? currentFileIndex + 1 : 0;
      onNavigateToFile(newIndex);
    }
  };

  useEffect(() => {
    if (!file) return;
    if (SUPPORTED_IMG_FILE_TYPES.includes(file.type)) setLoading(true);
  }, [file]);

  const renderPreview = () => {
    if (!file || !url) return null;

    // For mobile devices, show a button to open in new tab instead of iframe
    if (
      isMobile &&
      (file.type === "pdf" || SUPPORTED_DOC_FILE_TYPES.includes(file.type))
    ) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            padding: "20px",
            textAlign: "center",
          }}
        >
          <ThemedTypography
            variant="h6"
            sx={{ mb: 2, fontSize: "1.2rem", mx: "40px" }}
          >
            {file.name}
          </ThemedTypography>
          <Button
            variant="contained"
            startIcon={<OpenInNewIcon />}
            onClick={handleOpenInNewTab}
            sx={{ mb: 2 }}
          >
            Open in New Tab
          </Button>
        </Box>
      );
    }

    if (file.type === "pdf") {
      return (
        <iframe
          src={url}
          width="100%"
          height="100%"
          style={{
            border: 0,
            outline: 0,
            margin: 0,
            padding: 0,
            minHeight: "calc(100vh - 120px)",
          }}
          title={file.name}
          allowFullScreen
          sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox"
        >
          This browser does not support PDFs. Please download the PDF to view
          it: <a href={url}>Download PDF</a>.
        </iframe>
      );
    } else if (SUPPORTED_DOC_FILE_TYPES.includes(file.type)) {
      const officeViewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;

      return (
        <iframe
          src={officeViewerUrl}
          width="100%"
          height="100%"
          style={{
            border: 0,
            outline: 0,
            margin: 0,
            padding: 0,
            minHeight: "calc(100vh - 120px)",
          }}
          title={file.name}
          allowFullScreen
          sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox"
        >
          If you can't view the file, download it here:{" "}
          <a href={url}>Download File</a>.
        </iframe>
      );
    } else if (SUPPORTED_IMG_FILE_TYPES.includes(file.type)) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
          }}
        >
          <CircularProgress hidden={!loading} size="50px" />
          <img
            src={url}
            hidden={loading}
            onLoad={() => setLoading(false)}
            alt="Preview"
            style={{
              width: "100%",
              objectFit: "contain",
              border: 0,
              outline: 0,
              margin: 0,
              padding: 0,
            }}
          />
        </Box>
      );
    } else {
      console.error("Unsupported file type for previews");
    }
  };

  return (
    <Dialog
      fullScreen
      open={file !== null && url}
      onClose={handleClose}
      TransitionComponent={Transition}
      aria-labelledby="edit-project-dialog-title"
    >
      <ThemedDialogTitle
        id="edit-project-dialog-title"
        sx={{
          backgroundColor: darkThemeEnabled ? DARK_THEME_BODY_COLOR : "#f7f7f7",
          padding: "8px 16px",
          minHeight: "unset",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: "5px",
          }}
        >
          <div
            style={{
              color: darkThemeEnabled
                ? DARK_THEME_TEXT_COLOR
                : LIGHT_THEME_TEXT_COLOR,
              fontSize: "1rem",
              fontWeight: "bold",
              marginRight: "18px",
            }}
          >
            {file?.name}
            {files.length > 1 && (
              <span
                style={{
                  fontSize: "0.8rem",
                  fontWeight: "normal",
                  opacity: 0.7,
                }}
              >
                {" "}
                ({currentFileIndex + 1} of {files.length})
              </span>
            )}
          </div>
          <div>
            <IconButton
              edge="start"
              color="inherit"
              onClick={handleClose}
              aria-label="close"
              style={{
                color: darkThemeEnabled
                  ? DARK_THEME_TEXT_COLOR
                  : LIGHT_THEME_TEXT_COLOR,
              }}
            >
              <CloseIcon />
            </IconButton>
          </div>
        </div>
      </ThemedDialogTitle>
      <ThemedDialogContent
        sx={{
          backgroundColor: darkThemeEnabled ? DARK_THEME_BODY_COLOR : "#f7f7f7",
          px: 0,
          padding: 0,
          margin: 0,
          overflow: "hidden",
          position: "relative",
        }}
      >
        {renderPreview()}

        {/* Navigation arrows for multiple files */}
        {files.length > 1 && (
          <>
            {/* Previous button */}
            <IconButton
              onClick={handleNavigateToPrevious}
              sx={{
                position: "absolute",
                left: 15,
                top: "50%",
                transform: "translateY(-50%)",
                backgroundColor: "rgba(0, 0, 0, 0.35)",
                color: "white",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.55)",
                },
                zIndex: 1000,
              }}
            >
              <NavigateBeforeIcon />
            </IconButton>

            {/* Next button */}
            <IconButton
              onClick={handleNavigateToNext}
              sx={{
                position: "absolute",
                right: 15,
                top: "50%",
                transform: "translateY(-50%)",
                backgroundColor: "rgba(0, 0, 0, 0.35)",
                color: "white",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.55)",
                },
                zIndex: 1000,
              }}
            >
              <NavigateNextIcon />
            </IconButton>
          </>
        )}
      </ThemedDialogContent>
    </Dialog>
  );
}
